import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import DrawerComponent from "../../../components/Drawers/drawers";
import { getAllState, getAllLGA } from "../../../redux/Location/locationAction";
import { addStore, getStoreManagementStats } from "../../../redux/StoreManagement/storeManagementActions";

export const AddStoreDrawer = ({ showDrawer, setShowDrawer }) => {
  const dispatch = useDispatch();
  
  // Redux state
  const { stateData, stateLoading, lgaData, lgaLoading } = useSelector((state) => state.location);
  const { addStoreLoading, addStoreError } = useSelector((state) => state.storeManagement);
  
  const [storeData, setStoreData] = useState({
    name: "",
    state_id: "",
    lga_id: "",
    address: "",
    phone_number: "",
  });
  const [errors, setErrors] = useState({});

  // Load states on component mount
  useEffect(() => {
    if (showDrawer && (!stateData || stateData.length === 0)) {
      dispatch(getAllState());
    }
  }, [dispatch, showDrawer, stateData]);

  // Load LGAs when state is selected
  useEffect(() => {
    if (storeData.state_id) {
      dispatch(getAllLGA({ id: storeData.state_id }));
    }
  }, [dispatch, storeData.state_id]);

  const handleInputChange = (field, value) => {
    setStoreData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }

    // Reset LGA when state changes
    if (field === "state_id") {
      setStoreData(prev => ({
        ...prev,
        lga_id: ""
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!storeData.name.trim()) {
      newErrors.name = "Store name is required";
    }

    if (!storeData.state_id) {
      newErrors.state_id = "State is required";
    }

    if (!storeData.lga_id) {
      newErrors.lga_id = "LGA is required";
    }

    if (!storeData.address.trim()) {
      newErrors.address = "Address is required";
    }

    // Phone number validation (optional but if provided should be valid)
    if (storeData.phone_number && !/^[+]?[0-9\s\-()]{10,}$/.test(storeData.phone_number)) {
      newErrors.phone_number = "Please enter a valid phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    // Prepare payload for API
    const payload = {
      name: storeData.name.trim(),
      state_id: parseInt(storeData.state_id),
      lga_id: parseInt(storeData.lga_id),
      address: storeData.address.trim(),
      phone_number: storeData.phone_number.trim() || null,
    };

    try {
      const result = await dispatch(addStore(payload));
      
      if (addStore.fulfilled.match(result)) {
        // Reset form and close drawer on success
        setStoreData({
          name: "",
          state_id: "",
          lga_id: "",
          address: "",
          phone_number: "",
        });
        setErrors({});
        setShowDrawer(false);
        
        // Refresh store management stats after successful addition
        dispatch(getStoreManagementStats());
      }
    } catch (error) {
      // Error is handled in the action and reducer
      console.error("Error adding store:", error);
    }
  };

  return (
    <DrawerComponent
      show={showDrawer}
      showDrawer={setShowDrawer}
      title="Add Store"
    >
      <form onSubmit={handleSubmit} className="bg-white min-h-full overflow-y-auto mt-6">
        
        {/* Store Name */}
        <div className="mb-6">
          <label className="block text-[#3E473F] font-normal text-sm leading-[18px] mb-1">
            Name of the store
          </label>
          <input
            type="text"
            value={storeData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            className={`outline-none border ${
              errors.name ? "border-[#B92043]" : "border-[#96A397]"
            } w-full h-[44px] rounded-lg px-3 py-4 placeholder:text-[#96A397] focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5`}
            placeholder="Name of the store"
          />
          {errors.name && (
            <p className="text-[#B92043] text-xs mt-1">{errors.name}</p>
          )}
        </div>

        {/* State and LGA Row */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* State */}
          <div>
            <label className="block text-[#344335] font-normal text-sm leading-[18px] mb-1">
              State
            </label>
            <div className="relative">
              <select
                value={storeData.state_id}
                onChange={(e) => handleInputChange("state_id", e.target.value)}
                disabled={stateLoading}
                className={`outline-none border ${
                  errors.state_id ? "border-[#B92043]" : "border-[#96A397]"
                } w-full h-[44px] rounded-lg px-3 placeholder:text-[#96A397] focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5 bg-white appearance-none ${
                  stateLoading ? "disabled:bg-gray-100 disabled:cursor-not-allowed" : ""
                }`}
              >
                <option value="">{stateLoading ? "Loading..." : "Select State"}</option>
                {stateData?.map((state) => (
                  <option key={state.id} value={state.id}>
                    {state.name}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-4 h-4 text-[#96A397]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            {errors.state_id && (
              <p className="text-[#B92043] text-xs mt-1">{errors.state_id}</p>
            )}
          </div>

          {/* LGA */}
          <div>
            <label className="block text-[#344335] font-normal text-sm leading-[18px] mb-1">
              LGA
            </label>
            <div className="relative">
              <select
                value={storeData.lga_id}
                onChange={(e) => handleInputChange("lga_id", e.target.value)}
                disabled={!storeData.state_id || lgaLoading}
                className={`outline-none border ${
                  errors.lga_id ? "border-[#B92043]" : "border-[#96A397]"
                } w-full h-[44px] rounded-lg px-3 placeholder:text-[#96A397] focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5 bg-white appearance-none ${
                  !storeData.state_id || lgaLoading ? "disabled:bg-gray-100 disabled:cursor-not-allowed" : ""
                }`}
              >
                <option value="">
                  {!storeData.state_id ? "Select State first" : lgaLoading ? "Loading..." : "Select LGA"}
                </option>
                {lgaData?.map((lga) => (
                  <option key={lga.id} value={lga.id}>
                    {lga.name}
                  </option>
                ))}
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-4 h-4 text-[#96A397]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            {errors.lga_id && (
              <p className="text-[#B92043] text-xs mt-1">{errors.lga_id}</p>
            )}
          </div>
        </div>

        {/* Address */}
        <div className="mb-6">
          <label className="block text-[#344335] font-normal text-sm leading-[18px] mb-1">
            Address
          </label>
          <input
            type="text"
            value={storeData.address}
            onChange={(e) => handleInputChange("address", e.target.value)}
            className={`outline-none border ${
              errors.address ? "border-[#B92043]" : "border-[#96A397]"
            } w-full h-[44px] rounded-lg px-3 py-4 placeholder:text-[#96A397] focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5`}
            placeholder="Address"
          />
          {errors.address && (
            <p className="text-[#B92043] text-xs mt-1">{errors.address}</p>
          )}
        </div>

        {/* Phone Number */}
        <div className="mb-8">
          <label className="block text-[#344335] font-normal text-sm leading-[18px] mb-1">
            Phone Number (Optional)
          </label>
          <input
            type="tel"
            value={storeData.phone_number}
            onChange={(e) => handleInputChange("phone_number", e.target.value)}
            className={`outline-none border ${
              errors.phone_number ? "border-[#B92043]" : "border-[#96A397]"
            } w-full h-[44px] rounded-lg px-3 py-4 placeholder:text-[#96A397]  focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5`}
            placeholder="+234"
          />
          {errors.phone_number && (
            <p className="text-[#B92043] text-xs mt-1">{errors.phone_number}</p>
          )}
        </div>

        {/* API Error Display */}
        {addStoreError && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-red-600 text-sm">{addStoreError}</p>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={addStoreLoading}
          className="w-full outline-none disabled:bg-[#ABB6AC] disabled:border-[#ABB6AC] cursor-pointer bg-primary border border-primary text-white font-medium text-sm leading-5 py-[10px] px-[20px] rounded-[8px] hover:border hover:border-primary transition ease-in-out delay-100"
        >
          {addStoreLoading ? "Adding Store..." : "Add Store"}
        </button>
      </form>
    </DrawerComponent>
  );
};

export default AddStoreDrawer;